import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * Fired on `document` whenever any view changes the chosen set. Exported so the
 * sticky bar listens to a name rather than to a string it has to keep in step.
 */
export const SET_CHANGE_EVENT = 'lift:set-change';

/**
 * The buybox: picking a set swaps price, strikethrough, savings, button label
 * and the gallery slide in one go.
 *
 * Deliberately NOT Horizon's <variant-picker>. That one changes variants by
 * refetching the product URL and replacing the markup
 * (assets/variant-picker.js:296) — on the home page there is no copy of this
 * section under that URL, so there would be nothing to swap in.
 *
 * Nothing is calculated or formatted here. The section renders every variant's
 * money strings server-side into the JSON below; this class only moves them.
 *
 * @typedef {object} VariantData
 * @property {number} id
 * @property {string[]} options - Values in option order, e.g. ["Nude", "1 Rolle"].
 * @property {string} price - Already formatted, e.g. "14,99 €".
 * @property {string|null} compare
 * @property {string|null} save
 * @property {boolean} available
 * @property {number} mediaIndex - Gallery slide belonging to this variant.
 * @property {string|null} mediaUrl - Thumbnail for the fly-to-cart animation.
 *
 * A ref belongs to the NEAREST enclosing component (assets/component.js:171),
 * so `variantId`, `addToCartButton` and `buttonPrice` are product-form's and
 * add-to-cart's refs, never ours. Listing them in requiredRefs threw on every
 * connect and left this.refs empty. They are read with querySelector instead.
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data - The JSON payload.
 * @property {HTMLInputElement} [shade] - Carries the chosen shade.
 * @property {HTMLElement} [price]
 * @property {HTMLElement} [compare]
 * @property {HTMLElement} [compareRow]
 * @property {HTMLElement} [save]
 * @property {HTMLElement} [saveRow]
 * @property {HTMLImageElement} [thumb] - Set thumbnail, sheet and bar only.
 *
 * @extends {Component<Refs>}
 */
class LiftBuybox extends Component {
  requiredRefs = ['data'];

  /** @type {VariantData[]} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();
    this.#readVariants();
    document.addEventListener(SET_CHANGE_EVENT, this.#handleSetSync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(SET_CHANGE_EVENT, this.#handleSetSync);
  }

  /**
   * The theme editor re-renders a section through the Section Rendering API,
   * which hands back a fresh <script> element with fresh JSON.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#readVariants();
  }

  #readVariants() {
    try {
      const parsed = JSON.parse(this.refs.data.textContent ?? '[]');
      this.#variants = Array.isArray(parsed) ? parsed : [];
    } catch {
      // A malformed payload must not take the add-to-cart button down with it:
      // the form still carries the variant the server rendered.
      this.#variants = [];
    }
  }

  /**
   * Resolved from BOTH options, even while the shade has a single value.
   * Otherwise a second shade later would be a rebuild rather than an addition.
   *
   * @param {string} set
   * @returns {VariantData|undefined}
   */
  #findVariant(set) {
    const shade = this.refs.shade?.value;

    return this.#variants.find((variant) => {
      const options = variant.options ?? [];
      if (options.length < 2 || shade == null) return options[0] === set;
      return options[0] === shade && options[1] === set;
    });
  }

  /** Called from on:change on the fieldset. */
  handleSetChange() {
    // Not event.target: the delegation proxies it to the element carrying the
    // on:change attribute, which is the fieldset (assets/component.js:246).
    const checked = /** @type {HTMLInputElement|null} */ (
      this.querySelector('.lift-buybox__set-input:checked')
    );
    if (!checked) return;

    const variant = this.#findVariant(checked.value);
    if (!variant) return;

    this.#apply(variant);

    // The buybox, the sheet inside the sticky bar and the bar itself are three
    // views of one choice. The set value lets other buyboxes look the variant
    // up in their own payload; the variant rides along for the bar, which has
    // no payload of its own.
    document.dispatchEvent(
      new CustomEvent(SET_CHANGE_EVENT, {
        detail: { set: checked.value, variant, source: this },
      })
    );
  }

  /**
   * Another view changed the set. Pull our own radio across and follow.
   *
   * @param {Event} event
   */
  #handleSetSync = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;

    // Without this the two views would answer each other forever.
    if (!detail || detail.source === this) return;
    if (typeof detail.set !== 'string') return;

    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('.lift-buybox__set-input')
    );
    for (const input of inputs) {
      input.checked = input.value === detail.set;
    }

    const variant = this.#findVariant(detail.set);
    if (variant) this.#apply(variant);
  };

  /**
   * Moves one variant's already-formatted strings into the markup.
   *
   * Every lookup is a querySelector rather than a ref because this runs in two
   * shapes of markup: the full buybox and the sheet inside the sticky bar. A
   * missing element is a shape, not a fault.
   *
   * @param {VariantData} variant
   */
  #apply(variant) {
    const variantId = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="id"]')
    );
    if (variantId) variantId.value = String(variant.id);

    this.#setText(this.refs.price, variant.price);
    this.#setText(this.querySelector('.lift-buybox__submit-price'), variant.price);

    this.#setPair(this.refs.compareRow, this.refs.compare, variant.compare);
    this.#setPair(this.refs.saveRow, this.refs.save, variant.save);

    const button = /** @type {HTMLButtonElement|null} */ (
      this.querySelector('.lift-buybox__submit')
    );
    if (button) button.disabled = variant.available === false;

    // The sheet header carries a thumbnail of the chosen set.
    const thumb = this.refs.thumb;
    if (thumb instanceof HTMLImageElement && variant.mediaUrl) {
      thumb.src = variant.mediaUrl;
    }

    // Horizon reads this thumbnail when the button animates into the cart
    // (assets/product-form.js:111). Rendered once, so it would keep showing
    // the first variant's image without this.
    const addToCart = this.querySelector('add-to-cart-component');
    if (addToCart instanceof HTMLElement && variant.mediaUrl) {
      addToCart.dataset.productVariantMedia = variant.mediaUrl;
    }

    const gallery = this.querySelector('lift-gallery-component');
    if (gallery && 'select' in gallery && typeof gallery.select === 'function') {
      gallery.select(variant.mediaIndex);
    }
  }

  /**
   * @param {Element|null|undefined} element
   * @param {string} text
   */
  #setText(element, text) {
    if (element) element.textContent = text;
  }

  /**
   * A row hides whole — prefix included — when the variant has no comparison.
   *
   * @param {HTMLElement|undefined} row
   * @param {HTMLElement|undefined} value
   * @param {string|null} text
   */
  #setPair(row, value, text) {
    if (!row) return;

    if (text) {
      this.#setText(value, text);
      row.hidden = false;
    } else {
      row.hidden = true;
    }
  }
}

if (!customElements.get('lift-buybox-component')) {
  customElements.define('lift-buybox-component', LiftBuybox);
}

/**
 * The bottom sheet the sticky bar opens.
 *
 * Everything a dialog needs — showModal, focus trap, Escape, scroll lock,
 * click-outside — comes from DialogComponent. The one thing added is closing
 * after a successful add to cart: without it the sheet stays up, and because a
 * modal dialog sits in the top layer, Horizon's cart drawer cannot come
 * forward. Same handler as quick-add.js:326, for the same reason.
 */
class LiftSheet extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {any} event */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        // A failed add leaves the sheet up, where the error message is.
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lift-sheet] cart event rejected:', error);
      });
  };
}

if (!customElements.get('lift-sheet-component')) {
  customElements.define('lift-sheet-component', LiftSheet);
}
